// Practice: one Attempt = one learner working one checkpoint on one paper. Events are append-only and
// carry a client sequence number `n` (retries are deduplicated by (attemptId, n)). Coordinates are page px.
import { z } from 'zod';
import { TutorIntent } from './learning.ts';

const T = z.number().int().min(0);
const Point = z.tuple([z.number(), z.number()]);

export const PageEvent = z.object({ k: z.literal('page'), n: z.number().int().min(0), t: T, w: z.number().positive().max(10_000), h: z.number().positive().max(10_000) });
/** pts: [x, y, dt since t0, altitude, azimuth]. */
export const StrokeEvent = z.object({ k: z.literal('stroke'), n: z.number().int().min(0), id: z.string().max(40), tool: z.literal('pen'), t0: T, t1: T, pts: z.array(z.tuple([z.number(), z.number(), z.number(), z.number(), z.number()])).min(1).max(20_000) });
/** Pencil near the glass, not touching; pts: [x, y, dt since t0]. */
export const HoverEvent = z.object({ k: z.literal('hover'), n: z.number().int().min(0), t0: T, t1: T, pts: z.array(z.tuple([z.number(), z.number(), z.number()])).max(20_000) });
export const EraseEvent = z.object({ k: z.literal('erase'), n: z.number().int().min(0), t: T, t1: T.optional(), ids: z.array(z.string().max(40)).min(1).max(2000), via: z.enum(['eraser', 'undo']) });
export const SayEvent = z.object({ k: z.literal('say'), n: z.number().int().min(0), t: T, t1: T, text: z.string().max(4000) });
export const EndEvent = z.object({ k: z.literal('end'), n: z.number().int().min(0), t: T });
export const InkEvent = z.discriminatedUnion('k', [PageEvent, StrokeEvent, HoverEvent, EraseEvent, SayEvent, EndEvent]);

export const MarkInk = z.enum(['vermilion', 'cobalt', 'green']);
/** circle/underline use box [x, y, w, h]; arrow uses from/to; note uses at + text. Unused fields null. */
export const Mark = z.object({
  kind: z.enum(['circle', 'underline', 'arrow', 'note']),
  ink: MarkInk,
  box: z.tuple([z.number(), z.number(), z.number(), z.number()]).nullable(),
  from: Point.nullable(),
  to: Point.nullable(),
  at: Point.nullable(),
  text: z.string().max(40).nullable(),
});

export const CompetenceState = z.enum(['recognition', 'explanation', 'prediction', 'transfer', 'retention', 'calibration']);
/** Evidence the tutor reports; concept is a concept or link id from the checkpoint's `concepts`. */
export const EvidenceItem = z.object({ concept: z.string(), state: CompetenceState, ok: z.boolean() });

/** What one tutor turn returns (strict JSON from the model, validated and clamped server-side). */
export const Reply = z.object({
  say: z.string().max(2400),
  marks: z.array(Mark).max(3),
  verdict: z.enum(['open', 'partial', 'correct']),
  done: z.boolean(),
  evidence: z.array(EvidenceItem).max(12),
  /** Optional Mermaid diagram (rendered with Pele) when an explanation needs a picture; null otherwise. */
  diagram: z.string().max(4000).nullable(),
  /** The misconception this answer shows, copied verbatim from the checkpoint's `misconceptions`; null if none clearly applies. */
  misconception: z.string().max(400).nullable(),
});

export const Turn = z.discriminatedUnion('role', [
  z.object({ role: z.literal('learner'), turn: z.number().int().min(1), message: z.string().max(10_000).nullable(), at: z.string(), intent: TutorIntent.optional(), confidence: z.number().int().min(0).max(100).nullable().optional() }),
  z.object({ role: z.literal('tutor'), turn: z.number().int().min(1), at: z.string(), reply: Reply }),
]);

export const Attempt = z.object({
  id: z.string(),
  userId: z.string(),
  lessonId: z.string(),
  checkpointId: z.string(),
  createdAt: z.string(),
  status: z.enum(['open', 'done', 'abandoned']),
  /** Set when this attempt is a scheduled review (redraw) rather than a first pass. */
  reviewId: z.string().nullable(),
});

/** The learner's own replay marks ("I was stuck here"): ground truth, never shown to the tutor. */
export const StuckMark = z.object({ t: T, note: z.string().max(200), at: z.string() });

export type InkEvent = z.infer<typeof InkEvent>;
export type StrokeEvent = z.infer<typeof StrokeEvent>;
export type HoverEvent = z.infer<typeof HoverEvent>;
export type EraseEvent = z.infer<typeof EraseEvent>;
export type SayEvent = z.infer<typeof SayEvent>;
export type PageEvent = z.infer<typeof PageEvent>;
export type Mark = z.infer<typeof Mark>;
export type CompetenceState = z.infer<typeof CompetenceState>;
export type EvidenceItem = z.infer<typeof EvidenceItem>;
export type Reply = z.infer<typeof Reply>;
export type Turn = z.infer<typeof Turn>;
export type Attempt = z.infer<typeof Attempt>;
export type StuckMark = z.infer<typeof StuckMark>;
